import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { auth } from 'utils/firebaseConfig';
import Header from 'components/Header/Header';

function RedemptionPage(props) {
    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState({ totalPoints: 0, totalEarned: 0, totalSpent: 0 });
    const [countries, setCountries] = useState([]);
    const [selectedCountryId, setSelectedCountryId] = useState(null);
    const [partners, setPartners] = useState([]);
    const [selectedPartnerId, setSelectedPartnerId] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [showCountryList, setShowCountryList] = useState(false);
    const [showPartnerList, setShowPartnerList] = useState(false);
    const [showDonationModal, setShowDonationModal] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [donationTokens, setDonationTokens] = useState(1000);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successDetails, setSuccessDetails] = useState({ tokens: 0, usd: 0 });
    const [modalFocusedButton, setModalFocusedButton] = useState(0); // 0: Donate, 1: Donate All, 2: Cancel
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [donateSubmitting, setDonateSubmitting] = useState(false);
    const donateLockRef = useRef(false);

    // API Base URL - use environment variable
    // Remove /api suffix since we'll add it in the endpoint calls
    const BASE_URL = (process.env.REACT_APP_MUSIC_ENGINE_URL || 'http://localhost/Sheqonomi-MusicEngine/public/api').replace(/\/api$/, '');

    // Fetch real balance from API
    const fetchBalance = async () => {
        try {
            const token = localStorage.getItem('me_access_token');
            const response = await fetch(`${BASE_URL}/api/auth/user/points/balance`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            return data;
        } catch (err) {
            return { total_points: 0, total_earned: 0, total_spent: 0 };
        }
    };

    // Fetch countries from API
    const fetchCountries = async () => {
        try {
            const token = localStorage.getItem('me_access_token');
            const response = await fetch(`${BASE_URL}/api/redemption/countries`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            return data.countries || [];
        } catch (err) {
            return [];
        }
    };

    // Fetch partners for selected country
    const fetchPartners = async (countryId) => {
        try {
            const token = localStorage.getItem('me_access_token');
            const response = await fetch(`${BASE_URL}/api/redemption/partners/${countryId}`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            return data.partners || [];
        } catch (err) {
            return [];
        }
    };

    // Fetch campaigns for selected partner and country
    const fetchCampaigns = async (partnerId, countryId) => {
        try {
            const token = localStorage.getItem('me_access_token');
            const response = await fetch(`${BASE_URL}/api/redemption/campaigns/${partnerId}/${countryId}`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            return data.campaigns || [];
        } catch (err) {
            return [];
        }
    };

    // Simple data loading without complex optimizations
    const loadInitialData = useCallback(() => {
        fetchBalance().then(balanceData => {
            setBalance({
                totalPoints: balanceData.total_points || 0,
                totalEarned: balanceData.total_earned || 0,
                totalSpent: balanceData.total_spent || 0
            });
        }).catch(err => {
        });

        fetchCountries().then(countriesData => {
            setCountries(countriesData);
        }).catch(err => {
        });

        setPartners([]);
        setCampaigns([]);
        setLoading(false);
    }, []);

    useEffect(() => {
        // Block ONLY chunk requests, allow all API calls
        const originalFetch = window.fetch;
        window.fetch = function(...args) {
            const url = args[0];
            if (typeof url === 'string' && 
                url.includes('localhost:3000') && 
                (url.includes('.chunk.js') || url.includes('.chunk.js.map'))) {
                // Return empty response to prevent loading
                return Promise.resolve({
                    ok: true,
                    status: 200,
                    json: () => Promise.resolve({}),
                    text: () => Promise.resolve(''),
                    blob: () => Promise.resolve(new Blob())
                });
            }
            return originalFetch.apply(this, args);
        };
        
        loadInitialData();

        // Cleanup
        return () => {
            window.fetch = originalFetch;
        };
    }, [loadInitialData]);

    useEffect(() => {
        if (!showDonationModal) {
            document.addEventListener('keydown', onKeyDown);
            return () => { document.removeEventListener('keydown', onKeyDown); };
        }
    }, [focusedIndex, campaigns.length, showDonationModal]);

    useEffect(() => {
        if (showDonationModal) {
            document.addEventListener('keydown', onModalKeyDown);
            return () => { document.removeEventListener('keydown', onModalKeyDown); };
        }
    }, [showDonationModal, donationTokens, selectedCampaign, modalFocusedButton]);

    const onKeyDown = (e) => {
        if (e.key === 'Backspace' || e.key === 'ArrowRight') {
            props.history.goBack();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex((prev) => Math.min(prev + 1, campaigns.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const campaign = campaigns[focusedIndex];
            if (campaign && campaign.is_clickable) {
                handleCampaignClick(campaign);
            }
        }
    };

    const onModalKeyDown = (e) => {
        if (!selectedCampaign) return;

        const minTokens = selectedCampaign.min_tokens || 1;

        if (e.key === 'Backspace') {
            // If typing inside the input, do not close modal or navigate
            const target = e.target || {};
            const tag = (target.tagName || '').toUpperCase();
            if (tag === 'INPUT') {
                // Allow normal backspace inside the input
                e.stopPropagation();
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            setShowDonationModal(false);
            setSelectedCampaign(null);
            setDonationTokens(minTokens);
            setModalFocusedButton(0);
        } else if (e.key === 'SoftLeft') {
            e.preventDefault();
            e.stopPropagation();
            // Navigate to previous button
            setModalFocusedButton((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'SoftRight') {
            e.preventDefault();
            e.stopPropagation();
            // Navigate to next button
            setModalFocusedButton((prev) => Math.min(prev + 1, 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            if (modalFocusedButton === 0 && !donateSubmitting) {
                // Donate
                handleDonation();
            } else if (modalFocusedButton === 1) {
                // Cancel
                setShowDonationModal(false);
                setSelectedCampaign(null);
                setDonationTokens(minTokens);
                setModalFocusedButton(0);
            }
        }
    };

    const handleTokenInputChange = (e) => {
        const val = e.target.value;
        const num = parseInt(val, 10);
        const minTokens = selectedCampaign?.min_tokens || 1;
        const maxTokens = (() => {
            const goalAmount = selectedCampaign?.donation_details?.goal_amount || 0;
            const currentAmount = selectedCampaign?.donation_details?.current_amount || 0;
            const tokensNeededForGoal = (goalAmount - currentAmount) * (selectedCampaign?.token_to_usd_rate || 1000);
            return Math.min(balance.totalPoints, Math.max(0, tokensNeededForGoal));
        })();
        if (isNaN(num)) {
            setDonationTokens(minTokens);
        } else {
            setDonationTokens(Math.max(minTokens, Math.min(num, maxTokens || balance.totalPoints)));
        }
    };

    const backHandler = () => {
        props.history.push('/rewards');
    };

    // Simple country change handler
    const handleCountryChange = useCallback((countryId) => {
        setSelectedCountryId(countryId);
        setPartners([]);
        setSelectedPartnerId(null);
        setCampaigns([]);
        
        fetchPartners(countryId).then(partnersData => {
            setPartners(partnersData);
        }).catch(err => {
        });
    }, []);

    const handlePartnerChange = useCallback((partnerId) => {
        setSelectedPartnerId(partnerId);
        setCampaigns([]);
        
        fetchCampaigns(partnerId, selectedCountryId).then(campaignsData => {
            if (campaignsData && campaignsData.length > 0) {
                // Ensure numeric values are properly formatted
                const formattedCampaigns = campaignsData.map(campaign => ({
                    ...campaign,
                    donation_details: campaign.donation_details ? {
                        ...campaign.donation_details,
                        current_amount: parseFloat(campaign.donation_details.current_amount || 0),
                        goal_amount: parseFloat(campaign.donation_details.goal_amount || 0),
                        total_contributors: parseInt(campaign.donation_details.total_contributors || 0, 10)
                    } : null,
                    token_to_usd_rate: parseFloat(campaign.token_to_usd_rate || 1000),
                    min_tokens: parseInt(campaign.min_tokens || 1, 10)
                }));
                
                setCampaigns(formattedCampaigns);
            } else {
                setCampaigns([]);
            }
        }).catch(err => {
            setCampaigns([]);
        });
    }, [selectedCountryId]);

    // Prefer emoji for flags; derive from country code when possible
    const getFlagEmoji = (country) => {
        const raw = (country?.flag_emoji || country?.flag || country?.flag_icon || '').toString();
        // If API returned a path (e.g., /flags/xx.png), ignore it as emoji
        if (raw && !/\//.test(raw) && !/\.png|\.svg/i.test(raw)) {
            return raw;
        }
        const code = (country?.code || country?.iso_code || country?.country_code || '').toString().trim().toUpperCase();
        if (/^[A-Z]{2}$/.test(code)) {
            const A = 0x1F1E6;
            const a = 'A'.charCodeAt(0);
            const chars = [
                A + (code.charCodeAt(0) - a),
                A + (code.charCodeAt(1) - a)
            ];
            return String.fromCodePoint(...chars);
        }
        return '';
    };

    const getCountryLabel = (country) => {
        const emoji = getFlagEmoji(country);
        const name = (country?.name || '').toString();
        return emoji ? `${emoji} ${name}` : name;
    };

    const resolveUrl = (path) => {
        if (!path) return '';
        try {
            if (/^https?:\/\//i.test(path)) return path;
            const base = (BASE_URL || '').replace(/\/$/, '');
            const p = String(path).startsWith('/') ? path : `/${path}`;
            return `${base}${p}`;
        } catch (_) { return path; }
    };

    const getFlagRender = (country, size = 20) => {
        // No longer rendering flag icons since emoji is part of the country name
        return null;
    };

    const getPartnerLogo = (partner, width = 60, height = 22) => {
        const logo = (partner?.logo_url || '').toString();
        if (!logo) return null;
        const src = resolveUrl(logo);
        return (
            <img alt="" src={src} style={{ width: width, height: height, borderRadius: 4, objectFit: 'contain', border: 'none' }} />
        );
    };

    // Auto-select the only country, when there is exactly one
    useEffect(() => {
        if (Array.isArray(countries) && countries.length === 1 && !selectedCountryId) {
            const only = countries[0];
            if (only && only.id != null) {
                handleCountryChange(Number(only.id));
            }
        }
    }, [countries, selectedCountryId, handleCountryChange]);


    const handleCampaignClick = (campaign) => {
        if (!campaign.is_clickable) return;
        
        setSelectedCampaign(campaign);
        const minTokens = campaign.min_tokens || 1000;
        setDonationTokens(minTokens);
        setShowDonationModal(true);
    };

    const handleDonation = async (tokensToUse = null) => {
        if (!selectedCampaign) return;
        if (donateLockRef.current) return; // prevent double-submit
        donateLockRef.current = true;
        setDonateSubmitting(true);

        // Use provided tokens or current donationTokens
        const tokensAmount = tokensToUse !== null ? tokensToUse : donationTokens;

        const minTokens = selectedCampaign.min_tokens || 1000;
        if (tokensAmount < minTokens) {
            alert(`Minimum donation is ${minTokens} tokens`);
            return;
        }

        const goalAmount = selectedCampaign.donation_details?.goal_amount || 0;
        const currentAmount = selectedCampaign.donation_details?.current_amount || 0;
        const tokensNeededForGoal = (goalAmount - currentAmount) * (selectedCampaign.token_to_usd_rate || 1000);
        const maxTokens = Math.min(balance.totalPoints, Math.max(0, tokensNeededForGoal));

        if (tokensAmount > maxTokens) {
            alert(`Cannot donate more than ${maxTokens} tokens for this campaign.`);
            return;
        }

        try {
            // Real donation API call
            const token = localStorage.getItem('me_access_token');
            const response = await fetch(`${BASE_URL}/api/redemption/redeem`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    campaign_id: selectedCampaign.id,
                    tokens_amount: tokensAmount,
                    description: `Donated to ${selectedCampaign.name}`
                })
            });
            const result = await response.json();

            if (response.ok && result.success) {
                // Store success details for display
                const usdAmount = calculateUSD(tokensAmount);
                setSuccessDetails({ 
                    tokens: tokensAmount, 
                    usd: usdAmount,
                    campaign: selectedCampaign.name 
                });
                
                // Refresh balance after donation
                const balanceData = await fetchBalance();
                setBalance({
                    totalPoints: balanceData.total_points || 0,
                    totalEarned: balanceData.total_earned || 0,
                    totalSpent: balanceData.total_spent || 0
                });

                // Refresh campaigns after donation
                if (selectedPartnerId && selectedCountryId) {
                    fetchCampaigns(selectedPartnerId, selectedCountryId).then(campaignsData => {
                        // Re-format campaigns after refresh
                        if (campaignsData && campaignsData.length > 0) {
                            const formattedCampaigns = campaignsData.map(campaign => ({
                                ...campaign,
                                donation_details: campaign.donation_details ? {
                                    ...campaign.donation_details,
                                    current_amount: parseFloat(campaign.donation_details.current_amount || 0),
                                    goal_amount: parseFloat(campaign.donation_details.goal_amount || 0),
                                    total_contributors: parseInt(campaign.donation_details.total_contributors || 0, 10)
                                } : null,
                                token_to_usd_rate: parseFloat(campaign.token_to_usd_rate || 1000),
                                min_tokens: parseInt(campaign.min_tokens || 1, 10)
                            }));
                            setCampaigns(formattedCampaigns);
                        } else {
                            setCampaigns(campaignsData);
                        }
                    }).catch(err => {
                    });
                }
                
                // Close modal and show success message
                setShowDonationModal(false);
                setShowSuccessMessage(true);
                setSelectedCampaign(null);
                setDonationTokens(1000);
                setModalFocusedButton(0);
                
                // Auto-hide success message after 5 seconds
                setTimeout(() => {
                    setShowSuccessMessage(false);
                }, 5000);
            } else {
                alert(result.message || 'Donation failed. Please try again.');
            }
        } catch (err) {
            alert('Donation failed. Please try again. Error: ' + err.message);
        } finally {
            donateLockRef.current = false;
            setDonateSubmitting(false);
        }
    };

    const calculateUSD = (tokens) => {
        if (!selectedCampaign) return 0;
        const rate = selectedCampaign.token_to_usd_rate || 1000;
        return (tokens / rate).toFixed(2);
    };

    return (
        <>
            <Header 
                title='Redemption' 
                back={true} 
                backHandler={backHandler}
            />
            
            {loading && <div style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>}
            
            {!loading && (
                <div style={{ padding: '10px 15px', background: '#f5f5f5', minHeight: '100vh' }}>
                    {/* Balance Section removed: points shown in header top-right */}

                    {/* Country Selector - custom dropdown with flag image + name */}
                    <div style={{ background: '#ffffff', padding: '15px', borderRadius: '8px', marginBottom: '15px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', position: 'relative' }}>
                        <div
                            onClick={() => setShowCountryList((v) => !v)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', color: '#333', cursor: 'pointer', boxSizing: 'border-box' }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden', flex: '1 1 auto', minWidth: 0, paddingRight: 8 }}>
                                {(() => {
                                    const country = countries.find((c) => String(c.id) === String(selectedCountryId));
                                    return country ? (
                                        <>
                                            {getFlagRender(country, 18)}
                                            <span style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', flex: '1 1 auto', minWidth: 0 }}>{country.name}</span>
                                        </>
                                    ) : (
                                        <span>-- Select Country --</span>
                                    );
                                })()}
                            </div>
                            <span style={{ marginLeft: 4, flexShrink: 0 }}>▾</span>
                        </div>
                        {showCountryList && (
                            <div style={{ position: 'absolute', zIndex: 20, left: 15, right: 15, top: 62, background: '#fff', border: '1px solid #ddd', borderRadius: '6px', maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 10px rgba(0,0,0,0.06)' }}>
                                {countries.map((country) => (
                                    <div
                                        key={country.id}
                                        onClick={() => { setShowCountryList(false); handleCountryChange(Number(country.id)); }}
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer' }}
                                    >
                                        {getFlagRender(country, 16)}
                                        <span style={{ fontSize: 13 }}>{country.name}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Partner Selector - Only show when country is selected */}
                    {selectedCountryId && (
                        <div style={{ background: '#ffffff', padding: '15px', borderRadius: '8px', marginBottom: '15px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', position: 'relative' }}>
                            <div
                                onClick={() => setShowPartnerList((v) => !v)}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', color: '#333', cursor: 'pointer', boxSizing: 'border-box' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden', flex: '1 1 auto', minWidth: 0, paddingRight: 8 }}>
                                    {(() => {
                                        const partner = partners.find((p) => String(p.id) === String(selectedPartnerId));
                                        return partner ? (
                                            <>
                                                <div style={{ flexShrink: 0 }}>{getPartnerLogo(partner, 72, 24)}</div>
                                                <span style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', flex: '1 1 auto', minWidth: 0 }}>{partner.name}</span>
                                            </>
                                        ) : (
                                            <span>-- Select Partner --</span>
                                        );
                                    })()}
                                </div>
                                <span style={{ marginLeft: 4, flexShrink: 0 }}>▾</span>
                            </div>
                            {showPartnerList && (
                                <div style={{ position: 'absolute', zIndex: 20, left: 15, right: 15, top: 62, background: '#fff', border: '1px solid #ddd', borderRadius: '6px', maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 10px rgba(0,0,0,0.06)' }}>
                                    {partners.map((partner) => (
                                        <div
                                            key={partner.id}
                                            onClick={() => { setShowPartnerList(false); handlePartnerChange(Number(partner.id)); }}
                                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer' }}
                                        >
                                            {getPartnerLogo(partner, 60, 22)}
                                            <span style={{ fontSize: 13 }}>{partner.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Campaigns List - Only show when both country and partner are selected */}
                    {selectedCountryId && selectedPartnerId && (
                        <div style={{ marginTop: '10px' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: '#333' }}>Available Campaigns</h3>
                            
                            {!Array.isArray(campaigns) && (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#666', fontSize: '14px' }}>Loading campaigns...</div>
                            )}
                            
                            {Array.isArray(campaigns) && campaigns.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '30px', color: '#666', fontSize: '14px' }}>
                                    No campaigns available for this partner/country combination
                                    <br />
                                    <small style={{ fontSize: '12px', color: '#999' }}>
                                        Partner ID: {selectedPartnerId}, Country ID: {selectedCountryId}
                                    </small>
                                </div>
                            )}

                            {Array.isArray(campaigns) && campaigns.map((campaign, index) => (
                            <div 
                                key={campaign.id || index}
                                style={{ 
                                    background: '#ffffff', 
                                    padding: '15px', 
                                    borderRadius: '8px', 
                                    marginBottom: '12px', 
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', 
                                    cursor: campaign.is_clickable ? 'pointer' : 'not-allowed',
                                    opacity: campaign.is_clickable ? 1 : 0.6,
                                    border: focusedIndex === index ? '2px solid #667eea' : '2px solid transparent',
                                    transform: focusedIndex === index ? 'translateY(-2px)' : 'none'
                                }}
                                onClick={() => campaign.is_clickable && handleCampaignClick(campaign)}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '6px', flexWrap: 'wrap' }}>
                                    <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#333', margin: 0, flex: '1 1 auto', minWidth: 0 }}>{campaign.name}</h4>
                                    {campaign.status && (
                                        <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '12px', background: '#ffc107', color: '#333', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0 }}>{campaign.status}</span>
                                    )}
                                </div>
                                <p style={{ fontSize: '12px', color: '#666', lineHeight: '1.4', margin: '8px 0' }}>{campaign.description}</p>
                                
                                {campaign.donation_details && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: '10px 0', padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                            <span style={{ color: '#666', fontWeight: '500' }}>Progress:</span>
                                            <span style={{ color: '#333', fontWeight: '700' }}>${parseFloat(campaign.donation_details.current_amount || 0).toFixed(2)} / ${parseFloat(campaign.donation_details.goal_amount || 0).toFixed(2)}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                            <span style={{ color: '#666', fontWeight: '500' }}>Contributors:</span>
                                            <span style={{ color: '#333', fontWeight: '700' }}>{campaign.donation_details.total_contributors || 0}</span>
                                        </div>
                                    </div>
                                )}

                                {campaign.is_clickable && (
                                    <button style={{ width: '100%', padding: '10px', marginTop: '10px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                                        Donate SheqPoints
                                    </button>
                                )}
                            </div>
                        ))}
                        </div>
                    )}
                </div>
            )}

            {/* Donation Modal */}
            {showDonationModal && selectedCampaign && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '10px' }}>
                    <div style={{ background: '#ffffff', borderRadius: '10px', padding: '15px', maxWidth: '280px', width: '100%', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)' }}>
                        <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: '#333', textAlign: 'center' }}>Donate to {selectedCampaign.name}</h3>
                        
                        {/* Main Token Input - Center Focus */}
                        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '20px 15px', borderRadius: '8px', marginBottom: '12px', textAlign: 'center' }}>
                            <input 
                                type="number" 
                                inputMode="numeric"
                                min={selectedCampaign?.min_tokens || 1}
                                value={donationTokens}
                                onChange={handleTokenInputChange}
                                style={{ 
                                    width: '100%', 
                                    maxWidth: '100%', 
                                    padding: '8px 10px', 
                                    fontSize: '22px', 
                                    fontWeight: 700, 
                                    textAlign: 'center', 
                                    borderRadius: '6px', 
                                    border: '2px solid #fff', 
                                    color: '#333',
                                    boxSizing: 'border-box'
                                }}
                            />
                            <div style={{ fontSize: '13px', color: '#ffffff', opacity: 0.9, marginTop: '6px' }}>Tokens to Donate</div>
                        </div>

                        {/* Min/Max Info */}
                        <div style={{ fontSize: '10px', color: '#666', textAlign: 'center', marginBottom: '12px', lineHeight: '1.5', padding: '8px', background: '#f8f9fa', borderRadius: '6px' }}>
                            <div style={{ marginTop: '4px', fontSize: '10px', color: '#666' }}>
                                Min: {selectedCampaign.min_tokens || 1} | Max: {(() => {
                                    const goalAmount = selectedCampaign.donation_details?.goal_amount || 0;
                                    const currentAmount = selectedCampaign.donation_details?.current_amount || 0;
                                    const tokensNeededForGoal = (goalAmount - currentAmount) * (selectedCampaign.token_to_usd_rate || 1000);
                                    return Math.min(balance.totalPoints, Math.max(0, tokensNeededForGoal));
                                })()}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <button 
                                onClick={() => {
                                    setModalFocusedButton(0);
                                    if (!donateSubmitting) handleDonation();
                                }}
                                style={{ 
                                    flex: 1, 
                                    padding: '10px 8px', 
                                    background: donateSubmitting ? '#bdbdbd' : (modalFocusedButton === 0 ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e0e0e0'), 
                                    color: donateSubmitting ? '#666' : (modalFocusedButton === 0 ? '#ffffff' : '#333'), 
                                    border: modalFocusedButton === 0 ? '3px solid #ffc107' : '2px solid transparent',
                                    borderRadius: '6px', 
                                    fontSize: '11px', 
                                    fontWeight: '700',
                                    cursor: donateSubmitting ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            disabled={donateSubmitting}
                            >
                                {donateSubmitting ? 'Processing…' : 'Donate'}
                            </button>
                            <button 
                                onClick={() => {
                                    setShowDonationModal(false);
                                    setSelectedCampaign(null);
                                    setDonationTokens(selectedCampaign.min_tokens || 1000);
                                    setModalFocusedButton(0);
                                }}
                                style={{ 
                                    flex: 1, 
                                    padding: '10px 8px', 
                                    background: modalFocusedButton === 1 ? '#dc3545' : '#e0e0e0', 
                                    color: modalFocusedButton === 1 ? '#ffffff' : '#333', 
                                    border: modalFocusedButton === 1 ? '3px solid #ffc107' : '2px solid transparent',
                                    borderRadius: '6px', 
                                    fontSize: '11px', 
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Message */}
            {showSuccessMessage && (
                <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2000, background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', color: '#ffffff', padding: '12px', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)', textAlign: 'center', minWidth: '200px', maxWidth: '260px', width: '80%' }}>
                    <div style={{ fontSize: '36px', marginBottom: '6px' }}>✓</div>
                    <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '6px' }}>Donation Successful!</div>
                    <div style={{ fontSize: '12px', marginBottom: '3px' }}>You donated <strong>{successDetails.tokens}</strong> tokens</div>
                    <div style={{ fontSize: '12px', marginBottom: '3px' }}>≈ <strong>${successDetails.usd}</strong> USD</div>
                    <div style={{ fontSize: '10px', opacity: 0.9, marginTop: '6px' }}>to {successDetails.campaign}</div>
                    <div style={{ fontSize: '9px', opacity: 0.8, marginTop: '8px' }}>Thank you for your contribution!</div>
                </div>
            )}
        </>
    );
}

export default RedemptionPage;

